import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

// Reuse the same token caching from NotificationContext
var TOKEN_CACHE_KEY = function(userId) { return 'supa_token_v2_' + userId; };
var TOKEN_EXP_KEY   = function(userId) { return 'supa_token_v2_exp_' + userId; };

var getCachedToken = function(userId) {
  try {
    var token = localStorage.getItem(TOKEN_CACHE_KEY(userId));
    var exp = localStorage.getItem(TOKEN_EXP_KEY(userId));
    if (!token || !exp) return null;
    if (Date.now() >= parseInt(exp, 10)) return null;
    return token;
  } catch (e) {
    return null;
  }
};

var POLL_INTERVAL = 3000;
var POLL_INTERVAL_IDLE = 30000;

var JobProgressContext = createContext();

export var JobProgressProvider = function(props) {
  var children = props.children;
  var _activeJobs = useState([]);
  var activeJobs = _activeJobs[0];
  var setActiveJobs = _activeJobs[1];
  var mountedRef = useRef(true);
  var user = useSelector(function(state) { return state.user; });

  useEffect(function() {
    mountedRef.current = true;
    return function() { mountedRef.current = false; };
  }, []);

  var fetchJobs = function() {
    var supabaseToken = getCachedToken(user && user.id);
    if (!supabaseToken) return Promise.resolve([]);

    var restUrl = (process.env.REACT_APP_SUPABASE_URL || '') +
      '/rest/v1/job_progress?order=created_at.desc&select=*' +
      '&created_at=gte.' + new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    return fetch(restUrl, {
      headers: {
        'Authorization': 'Bearer ' + supabaseToken,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || ''
      }
    })
      .then(function(res) { return res.ok ? res.json() : []; })
      .catch(function() { return []; });
  };

  useEffect(function() {
    if (!user || !user.id) return;

    var timerId = null;

    var poll = function() {
      fetchJobs().then(function(rows) {
        if (!mountedRef.current) return;

        setActiveJobs(function(prev) {
          // Merge: keep dismissed jobs out (jobs the user already dismissed)
          // A job is "visible" if it's in the fresh rows and wasn't manually dismissed
          // We track dismissed IDs via a simple check: if a completed/failed job
          // was in prev but is NOT in the new fetch, it was dismissed. But since
          // REST always returns all 24h jobs, we need a different approach.
          // Simple: just replace with fresh data, but preserve dismiss state
          // by filtering out IDs the user dismissed.
          return rows;
        });

        // Poll faster while there are active (queued/processing) jobs
        var hasActive = rows.some(function(j) {
          return j.status === 'queued' || j.status === 'processing';
        });
        var interval = hasActive ? POLL_INTERVAL : POLL_INTERVAL_IDLE;

        timerId = setTimeout(poll, interval);
      });
    };

    // Initial fetch
    poll();

    return function() {
      if (timerId) clearTimeout(timerId);
    };
  }, [user && user.id]);

  var dismissJob = function(jobId) {
    setActiveJobs(function(prev) {
      return prev.filter(function(j) { return j.id !== jobId; });
    });
  };

  var cancelJob = function(jobId) {
    // Optimistically mark as cancelled locally, then PATCH Supabase
    setActiveJobs(function(prev) {
      return prev.map(function(j) {
        if (j.id === jobId) {
          return Object.assign({}, j, { status: 'cancelled', _dismissing: true });
        }
        return j;
      });
    });

    // PATCH Supabase to signal backend to stop
    var supabaseToken = getCachedToken(user && user.id);
    if (supabaseToken) {
      var patchUrl = (process.env.REACT_APP_SUPABASE_URL || '') +
        '/rest/v1/job_progress?id=eq.' + jobId;

      fetch(patchUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': 'Bearer ' + supabaseToken,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({ status: 'cancelled', message: 'Cancelado pelo usuário', updated_at: new Date().toISOString() })
      }).catch(function() { /* best effort */ });
    }

    // Dismiss after short delay for animation
    setTimeout(function() { dismissJob(jobId); }, 400);
  };

  // Only expose non-dismissed, recent jobs
  var visibleJobs = activeJobs.filter(function(j) {
    if (j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled') {
      var updatedAt = new Date(j.updated_at).getTime();
      if (Date.now() - updatedAt > 5 * 60 * 1000) return false;
    }
    return true;
  });

  return React.createElement(
    JobProgressContext.Provider,
    {
      value: {
        activeJobs: visibleJobs,
        dismissJob: dismissJob,
        cancelJob: cancelJob
      }
    },
    children
  );
};

export var useJobProgress = function() {
  var context = useContext(JobProgressContext);
  if (!context) {
    throw new Error('useJobProgress must be used within a JobProgressProvider');
  }
  return context;
};
